import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import SignupConfirmationPage from '@/pages/SignupConfirmationPage';
import ActivateReferralPage from '@/pages/ActivateReferralPage';
import Dashboard from '@/pages/Dashboard';
import AdminRoutes from './AdminRoutes';
import MasterAdminRoutes from './MasterAdminRoutes';
import SuperAdminRoutes from './SuperAdminRoutes';
import ResellerRoutes from './ResellerRoutes';
import UserRoutes from './UserRoutes';
import BusinessRoutes from './BusinessRoutes';
import NotFound from '@/pages/NotFound';
import PrivateRoute from '@/components/hoc/PrivateRoute';
import MasterLicenseRoutes from './MasterLicenseRoutes';
import Login from '@/pages/Login';
import Index from '@/pages/Index';
import PublicRoutes from './PublicRoutes';
import BlogPage from '@/pages/BlogPage';
import BlogPostDetail from '@/pages/BlogPostDetail';
import { useAuth } from '@/context/SupabaseAuthContext';
import { Toaster } from '@/components/ui/toaster';
import GoogleCalendarSuccess from '@/pages/GoogleCalendarSuccess';
import AuthPage from '@/pages/AuthPage';

const AppRoutes: React.FC = () => {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  
  return (
    <>
      <Toaster />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Index />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:id" element={<BlogPostDetail />} />
        
        {/* Public Routes */}
        <Route path="/*" element={<PublicRoutes isLoggedIn={isAuthenticated} />} />
        
        {/* New Supabase Auth Route */}
        <Route path="/auth" element={!isAuthenticated ? <AuthPage /> : <Navigate to="/dashboard" replace />} />
        
        {/* Redirect old login routes to new auth page */}
        <Route path="/login" element={!isAuthenticated ? <AuthPage /> : <Navigate to="/dashboard" replace />} />
        <Route path="/login-page" element={<Navigate to="/auth" replace />} />
        <Route path="/signup-form" element={<Navigate to="/auth" replace />} />
        <Route path="/signup-confirmation" element={<SignupConfirmationPage />} />
        <Route path="/activate-referral" element={<ActivateReferralPage />} />
        
        {/* Redirect from /dashboard to role-specific dashboard */}
        <Route path="/dashboard" element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        } />

        {/* Redirect from /admin-dashboard to /admin/dashboard */}
        <Route path="/admin-dashboard" element={<Navigate to="/admin/dashboard" replace />} />
        
        {/* User Routes */}
        <Route path="/user/*" element={
          <PrivateRoute requiredRole="user">
            <UserRoutes />
          </PrivateRoute>
        } />
        
        {/* Admin Routes */}
        <Route path="/admin/*" element={
          <PrivateRoute requiredRole="admin">
            <AdminRoutes />
          </PrivateRoute>
        } />
        
        {/* Super Admin Routes - single path for consistency */}
        <Route path="/super-admin/*" element={
          <PrivateRoute requiredRole="superadmin">
            <SuperAdminRoutes userRole="superadmin" />
          </PrivateRoute>
        } />
        
        {/* Redirect any /superadmin/* paths to /super-admin/* for consistency */}
        <Route path="/superadmin/*" element={
          <Navigate to="/super-admin/dashboard" replace />
        } />
        
        {/* Master Admin Routes */}
        <Route path="/master/*" element={
          <PrivateRoute requiredRole="masterAdmin">
            <MasterLicenseRoutes />
          </PrivateRoute>
        } />
        
        {/* Add a direct route for master-license to fix the issue */}
        <Route path="/master-license/*" element={
          <PrivateRoute requiredRole="masterAdmin">
            <MasterLicenseRoutes />
          </PrivateRoute>
        } />
        
        {/* Redirect from /master-admin/* to /master/* for consistency */}
        <Route path="/master-admin/*" element={
          <Navigate to="/master/dashboard" replace />
        } />
        
        {/* Business Routes */}
        <Route path="/business/*" element={
          <PrivateRoute requiredRole="business">
            <BusinessRoutes isLoggedIn={isAuthenticated} userRole="business" />
          </PrivateRoute>
        } />
        
        {/* Reseller Routes */}
        <Route path="/reseller/*" element={
          <PrivateRoute requiredRole="reseller">
            <ResellerRoutes />
          </PrivateRoute>
        } />
        
        {/* Not Found Route */}
        <Route path="*" element={<NotFound />} />
        
        {/* Add the Google Calendar Success route */}
        <Route path="/master-license/calendar-success" element={<GoogleCalendarSuccess />} />
      </Routes>
    </>
  );
};

export default AppRoutes;
